import React, { useState } from 'react';
import {
  Box,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Link } from '@marageti/z4-lib';
import { GoogleLoginRequest } from '@marageti/z4-sdk/lib/auth/Token';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { ReactComponent as ZicassoSVG } from '../../../../assets/svg/zicasso-logo-redesign.svg';
import GoogleLoginView from '../../../../components/auth/google-sso-button';
import SuccessCheckSparkle from '../../../../components/success-check-sparkle';
import config from '../../../../config';
import FieldError, { errorOutlinedInputSx } from '../../field-error';
import { isValidLoginEmail } from '../../helpers';
import { linkSx, solidButtonSx } from '../../styles';

const GOOGLE_LOGIN_ERROR_MESSAGE = "We couldn't sign you in with Google. Try your email instead.";

const googleLoginErrorDetail = (error: ApiError): string | undefined => {
  if (!error.code) {
    return undefined;
  }
  return `Code: ${error.code} ${error.message || 'Please contact support'}`;
};

export interface UserIdentificationViewProps {
  email?: string | null;
  isFromTripRequest?: boolean;
  isSubmitting?: boolean;
  loginError?: ApiError;
  onContinue: (email: string) => void | Promise<void>;
  onGoogleLogin: (request: GoogleLoginRequest) => void;
}

/**
 * Collect email (or Google SSO), then Continue.
 * Next step is chosen via getDefaultLoginMethod.
 */
const UserIdentificationView = ({
  email: initialEmail = '',
  isFromTripRequest = false,
  isSubmitting = false,
  loginError,
  onContinue,
  onGoogleLogin,
}: UserIdentificationViewProps): React.ReactElement => {
  const [email, setEmail] = useState(initialEmail || '');
  const [touched, setTouched] = useState(false);
  const isValid = isValidLoginEmail(email);
  const showError = touched && !isValid;

  const handleContinue = async (event?: React.FormEvent) => {
    event?.preventDefault();
    setTouched(true);
    if (!isValid || isSubmitting) {
      return;
    }
    await onContinue(email.trim());
  };

  return (
    <Stack
      spacing="40px"
      alignItems="center"
      width="100%"
      sx={{
        flex: 1,
        minHeight: 0,
        justifyContent: 'space-between',
      }}
    >
      <Stack spacing="40px" alignItems="center" width="100%">
        {/* Match email/SMS header: reserve IconButton row so the logo aligns with step titles. */}
        <Stack spacing={2} width="100%">
          <Box
            aria-hidden
            sx={{
              height: 40,
              width: '100%',
              flexShrink: 0,
            }}
          />
          <Stack spacing={2} alignItems="center" width="100%">
            {!isFromTripRequest && (
              <>
                <ZicassoSVG aria-label="Zicasso" width={134} height="100%" />
                <Typography color="text.secondary" textAlign="center">
                  Sign in to get back to your trip.
                </Typography>
              </>
            )}
            {isFromTripRequest && (
              <>
                <SuccessCheckSparkle />
                <Typography
                  component="h1"
                  variant="h2"
                  color="text.primary"
                  sx={{ textAlign: 'center', fontWeight: 700 }}
                >
                  We&apos;ve Received Your Request
                </Typography>
                <Typography color="text.secondary" sx={{ textAlign: 'center' }}>
                  We&apos;re reviewing your trip and our specialists will be in touch shortly.
                </Typography>
              </>
            )}
          </Stack>
        </Stack>

        <Box
          component="form"
          onSubmit={handleContinue}
          noValidate
          width="100%"
        >
          <Stack spacing={3} width="100%">
            <Stack spacing={1} width="100%">
              <TextField
                id="login-email"
                label="E-mail"
                type="email"
                name="email"
                autoComplete="username"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={showError}
                InputProps={{
                  readOnly: isFromTripRequest && !!initialEmail,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                    ...(showError && errorOutlinedInputSx),
                  },
                }}
              />
              {showError && (
                <FieldError message="Please enter a valid email" />
              )}
            </Stack>
            <LoadingButton
              type="submit"
              variant="contained"
              fullWidth
              loading={isSubmitting}
              disabled={isSubmitting}
              sx={solidButtonSx}
            >
              Continue
            </LoadingButton>
          </Stack>
        </Box>

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Divider sx={{ flex: 1 }} />
          <Typography variant="body2" margin="0 12px">Or</Typography>
          <Divider sx={{ flex: 1 }} />
        </Box>

        <Box sx={{ width: '100%' }}>
          <GoogleLoginView
            onSuccess={onGoogleLogin}
            error={loginError
              ? (
                <FieldError
                  message={GOOGLE_LOGIN_ERROR_MESSAGE}
                  detail={googleLoginErrorDetail(loginError)}
                />
              )
              : undefined}
          />
        </Box>
      </Stack>

      <Typography
        textAlign="center"
        sx={{
          fontFamily: 'Satoshi, sans-serif',
          fontWeight: 400,
          fontSize: '14px',
          lineHeight: '20px',
          color: '#4D4D4C',
        }}
      >
        Travel Specialists
        {' '}
        <Link
          to={config.PARTNERS_URL}
          underline="always"
          sx={{
            ...linkSx,
            fontSize: '14px',
            lineHeight: '20px',
          }}
        >
          Sign In Here
        </Link>
      </Typography>
    </Stack>
  );
};

export default UserIdentificationView;
